import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { BrandMark } from './BrandMark.js';
import { useAuthStore } from '../lib/auth.js';

const DOCS_URL = 'https://docs.boletra.com';

export function Layout({ children }: { children: ReactNode }) {
  const { lang } = useParams();
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // Lock background scroll while the mobile menu is open so it feels like a
  // proper sheet instead of a flaky drawer.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-neutral-900/80 sticky top-0 backdrop-blur bg-neutral-950/70 z-30">
        <nav className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-6">
          <Link
            to={`/${lang}`}
            className="flex items-center gap-2 group"
            onClick={() => setOpen(false)}
            aria-label={t('brand.name')}
          >
            <BrandMark size={28} />
            <span className="font-bold text-lg tracking-tight group-hover:text-[var(--color-accent)] transition">
              {t('brand.name')}
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-1 ml-2">
            <NavItem to={`/${lang}/discover`}>{t('nav.events')}</NavItem>
            <NavItem to={`/${lang}#how-it-works`}>{t('nav.how_it_works')}</NavItem>
            <NavItem to={`/${lang}#pricing`}>{t('nav.pricing')}</NavItem>
            <NavItem href={DOCS_URL}>{t('nav.docs')}</NavItem>
          </div>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <div className="hidden md:flex items-center gap-3">
              <LanguageSwitcher />
              {user ? (
                <Link to={`/${lang}/dashboard`} className="btn-secondary !py-2 !px-4 text-sm">
                  {t('nav.dashboard')}
                </Link>
              ) : (
                <>
                  <Link
                    to={`/${lang}/auth/login`}
                    className="text-sm text-neutral-300 hover:text-neutral-50 transition px-3 py-2"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link to={`/${lang}/auth/signup`} className="btn-primary !py-2 !px-4 text-sm">
                    {t('nav.signup')}
                  </Link>
                </>
              )}
            </div>
            <div className="md:hidden">
              <LanguageSwitcher />
            </div>
            <button
              type="button"
              aria-label={open ? t('actions.close') : 'Menu'}
              aria-expanded={open}
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-neutral-800 hover:border-neutral-600 transition"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </nav>
        {open && (
          <div className="md:hidden border-t border-neutral-900 bg-neutral-950">
            <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col gap-1 text-sm">
              <MobileLink to={`/${lang}/discover`}>{t('nav.events')}</MobileLink>
              <MobileLink to={`/${lang}#how-it-works`}>{t('nav.how_it_works')}</MobileLink>
              <MobileLink to={`/${lang}#pricing`}>{t('nav.pricing')}</MobileLink>
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-3 rounded-lg text-neutral-300 hover:text-neutral-50 hover:bg-neutral-900"
              >
                {t('nav.docs')}
              </a>
              <div className="pt-3 mt-2 border-t border-neutral-900 grid gap-2">
                {user ? (
                  <Link to={`/${lang}/dashboard`} className="btn-primary w-full">
                    {t('nav.dashboard')}
                  </Link>
                ) : (
                  <>
                    <Link to={`/${lang}/auth/signup`} className="btn-primary w-full">
                      {t('nav.signup')}
                    </Link>
                    <Link to={`/${lang}/auth/login`} className="btn-secondary w-full">
                      {t('nav.login')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-neutral-900 py-10 text-sm text-neutral-500">
        <div className="max-w-6xl mx-auto px-4 grid gap-6 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Link to={`/${lang}`} className="inline-flex items-center gap-2 mb-3">
              <BrandMark size={24} />
              <span className="font-bold text-base text-neutral-200">{t('brand.name')}</span>
            </Link>
            <p className="text-neutral-500 leading-relaxed text-xs max-w-xs">
              {t('footer.tagline', {
                defaultValue: t('brand.tagline'),
              })}
            </p>
          </div>
          <FooterCol title={t('nav.events')}>
            <Link to={`/${lang}/discover`} className="hover:text-neutral-300">
              {t('nav.events')}
            </Link>
            <Link to={`/${lang}#pricing`} className="hover:text-neutral-300">
              {t('nav.pricing')}
            </Link>
            <Link to={`/${lang}#how-it-works`} className="hover:text-neutral-300">
              {t('nav.how_it_works')}
            </Link>
          </FooterCol>
          <FooterCol title={t('footer.product', { defaultValue: 'Product' })}>
            <Link to={`/${lang}/auth/signup`} className="hover:text-neutral-300">
              {t('nav.signup')}
            </Link>
            <a href={DOCS_URL} target="_blank" rel="noreferrer" className="hover:text-neutral-300">
              {t('nav.docs')}
            </a>
          </FooterCol>
          <FooterCol title={t('footer.legal', { defaultValue: 'Legal' })}>
            <Link to={`/${lang}/legal/privacy`} className="hover:text-neutral-300">
              {t('footer.privacy')}
            </Link>
            <Link to={`/${lang}/legal/terms`} className="hover:text-neutral-300">
              {t('footer.terms')}
            </Link>
          </FooterCol>
        </div>
        <div className="max-w-6xl mx-auto px-4 mt-8 pt-6 border-t border-neutral-900 text-xs text-neutral-600">
          {t('footer.rights')}
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, href, children }: { to?: string; href?: string; children: ReactNode }) {
  const cls =
    'px-3 py-2 rounded-lg text-sm text-neutral-400 hover:text-neutral-50 hover:bg-neutral-900/60 transition';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to!} className={cls}>
      <>{children}</>
    </Link>
  );
}

function MobileLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="px-3 py-3 rounded-lg text-neutral-300 hover:text-neutral-50 hover:bg-neutral-900 transition"
    >
      <>{children}</>
    </Link>
  );
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-neutral-300 font-semibold text-xs uppercase tracking-wider mb-3">
        {title}
      </p>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
